// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Dependency-free parsers for the two Australian recall registers.
//
// This file imports NOTHING. The test suite imports it directly, so it must
// stay runnable under plain `node` with no install step — a pipeline whose
// parsers can only be exercised through a network fetch is a pipeline with no
// tests at all.
//
// Both sources are server-rendered HTML, not an API. That is a deliberate
// constraint on how the parsing is written: everything below anchors on Drupal
// field machine-names (`field--name-field-psa-recall-hazards`) and on the
// vehicle register's label/value <dt>/<dd>-style structure, never on visual
// classes or element ordering, because those are what a CMS theme upgrade
// changes.

/* ────────────────────────────── HTML helpers ─────────────────────────────── */

const ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  ndash: '–', mdash: '—', hellip: '…', trade: '™',
  reg: '®', copy: '©', deg: '°', rsquo: '’', lsquo: '‘',
  ldquo: '“', rdquo: '”', eacute: 'é', middot: '·',
};

export function decodeEntities(s) {
  if (!s) return '';
  return s
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => safeCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => safeCodePoint(parseInt(d, 10)))
    .replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (m, name) => {
      const k = name.toLowerCase();
      return Object.prototype.hasOwnProperty.call(ENTITIES, k) ? ENTITIES[k] : m;
    });
}

function safeCodePoint(n) {
  // An out-of-range or surrogate code point throws from String.fromCodePoint and
  // would take the whole crawl down for one malformed entity in one product
  // description. Drop it instead.
  if (!Number.isFinite(n) || n < 0 || n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff)) return '';
  return String.fromCodePoint(n);
}

/**
 * Strip tags to readable text.
 *
 * Block-level tags become newlines BEFORE the strip, otherwise
 * `<li>Stop using it</li><li>Contact the supplier</li>` collapses into
 * "Stop using itContact the supplier" — two instructions welded into one
 * nonsense word, on the field that tells a reader what to actually DO about a
 * dangerous product.
 */
export function htmlToText(html) {
  if (!html) return '';
  return decodeEntities(
    String(html)
      .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|tr|h[1-6]|dd|dt|section)>/gi, '\n')
      .replace(/<li\b[^>]*>/gi, '\n• ')
      .replace(/<[^>]+>/g, ' '),
  )
    .replace(/[ \t ]+/g, ' ')
    .replace(/ *\n[ \n]*/g, '\n')
    .trim();
}

export function collapse(s) {
  return String(s || '').replace(/\s+/g, ' ').trim();
}

/**
 * Extract the inner HTML of the Drupal field block whose machine name is
 * `name`, from the opening tag through to the start of the NEXT field block at
 * any depth.
 *
 * This is a deliberately blunt slice rather than a DOM walk: the fields are
 * siblings in the rendered output and each one's content ends where the next
 * one begins. It is bounded by the end of the document for the final field.
 */
export function fieldBlock(html, name) {
  const open = new RegExp(`<(div|span|p|section)[^>]*class="([^"]*field--name-${name}(?![a-z0-9-])[^"]*)"[^>]*>`, 'i');
  const m = open.exec(html);
  if (!m) return null;
  const start = m.index + m[0].length;

  /*
   * A `field--label-hidden` field carries `field__item` on the field element
   * ITSELF, so there is no inner item to read and fieldText falls through to
   * "the whole block". Slicing that block to the next `field--name-` is wrong
   * for exactly those fields: anything between this field's closing tag and the
   * next field — a section heading, most commonly — gets swept in.
   *
   * That put the words "Identifying product features" on the end of 2,880
   * traders values (35% of the register), which then became retailer nodes in
   * the supplier network reading "BIG W Identifying product features" — a
   * duplicate of Big W that split one chain across two nodes. Closing the tag
   * properly, by counting nesting, ends the field where the field ends.
   */
  if (/field__item/.test(m[2]) && m[1].toLowerCase() === 'div') {
    return divContent(html, start);
  }

  const rest = html.slice(start);
  const next = /<[a-z]+[^>]*class="[^"]*field--name-[a-z0-9-]+/i.exec(rest);
  const end = next ? next.index : rest.length;
  return rest.slice(0, end);
}

/**
 * The rendered text of a field, with its own visible LABEL removed.
 *
 * Drupal renders `field--label-above` fields as "<div>Label</div><div
 * class="field__item">value</div>". Taking the whole block's text yields
 * "The hazards to consumers Risk of drowning…" — the label welded onto the
 * front of every value. Reading only `field__item` children drops the label
 * without needing to know what any label says.
 */
/**
 * The inner HTML of the <div> whose opening tag ends at `from`, found by
 * counting nested div tags.
 *
 * The obvious alternative — "everything up to the next sibling, or the end of
 * the document" — is what the first version did, and it breaks on the LAST
 * field of a page: with no following field to stop at, that field absorbed the
 * entire remainder of the document. On a real recall page it quietly appended
 * the category badges to the manufacturing country, producing values like
 * "China Kids' toys". Counting the tags costs a few lines and cannot do that.
 */
function divContent(html, from) {
  let depth = 1;
  let i = from;
  const tag = /<(\/?)div\b[^>]*?(\/?)>/gi;
  tag.lastIndex = from;
  for (let m = tag.exec(html); m; m = tag.exec(html)) {
    if (m[2] === '/') continue;          // a self-closing <div/> changes nothing
    depth += m[1] === '/' ? -1 : 1;
    if (depth === 0) return html.slice(from, m.index);
    i = tag.lastIndex;
  }
  return html.slice(from, i);
}

export function fieldText(html, name) {
  const block = fieldBlock(html, name);
  if (block == null) return '';
  const open = /<div[^>]*class="[^"]*field__item[^"]*"[^>]*>/gi;
  const items = [];
  for (let m = open.exec(block); m; m = open.exec(block)) {
    const inner = divContent(block, m.index + m[0].length);
    const text = htmlToText(inner);
    if (text) items.push(text);
    // Skip past this item so nested field__items are not read a second time.
    open.lastIndex = m.index + m[0].length + inner.length;
  }
  if (items.length) return items.join('\n');
  // `field--label-hidden` fields put the value directly on the field element.
  return htmlToText(block);
}

/* ─────────────────────── ACCC consumer product recalls ────────────────────── */

/**
 * The "N - M of TOTAL items shown" counter.
 *
 * This is the load-bearing number for the pager gate. The register's listing is
 * an undocumented Drupal Views AJAX endpoint where `page` is honoured only in
 * the QUERY STRING — put it in the POST body and every request returns HTTP 200
 * with a valid-looking response for page 0. A crawler that trusts its own loop
 * counter scrapes page 1 three hundred times and reports success. Reading the
 * range back off each response and asserting it advances is the only thing that
 * catches it.
 */
export function parseCounter(html) {
  const m = /([\d,]+)\s*-\s*([\d,]+)\s+of\s+([\d,]+)\s+items/i.exec(html);
  if (!m) return null;
  const num = (s) => Number(s.replace(/,/g, ''));
  return { from: num(m[1]), to: num(m[2]), total: num(m[3]) };
}

/**
 * Recall detail pages live under MORE THAN ONE path.
 *
 * Most are `/search-consumer-product-recalls/{slug}`, but a scattering across
 * the whole history — "/recalls/ch-pc-pump", "/recalls/girls-licensed-nighties"
 * — sit directly under `/recalls/{slug}` and render exactly the same fields.
 *
 * The first version of this parser accepted only the common prefix and dropped
 * the rest on the floor. It cost one recall in 2006 and one in 2007, which is
 * small enough to have passed completely unnoticed had the crawl not been
 * checked year-by-year against the register's own per-year totals. That is the
 * entire argument for slicing: a global "8,216 of 8,218" is a rounding error
 * nobody investigates, whereas "2006 → 246/247" points at a specific year with
 * a specific missing row.
 *
 * The KEY is therefore the full path, not the last segment: two recalls could
 * legitimately share a final segment under different prefixes, and collapsing
 * them would silently merge two different products.
 */
export function recallKeyFromHref(href) {
  if (!href || !href.startsWith('/')) return null;
  const path = href.split(/[?#]/)[0].replace(/\/+$/, '');
  const segments = path.slice(1).split('/');
  if (segments.length < 2) return null;
  let key;
  try { key = decodeURIComponent(segments.join('/')); } catch { key = segments.join('/'); }
  return key || null;
}

/**
 * One card per recall in the listing response.
 *
 * Returns skipped cards as well as accepted ones so the caller can assert that
 * every card the server rendered was accounted for — a parser that silently
 * returns fewer rows than the page contains is exactly the failure above.
 */
export function parseListingPage(html) {
  const out = [];
  const skipped = [];
  const cards = html.split(/<div\s+class="card-wrapper/i).slice(1);
  for (const card of cards) {
    const urlM = /data_url="([^"]+)"/i.exec(card);
    if (!urlM) { skipped.push({ reason: 'no data_url', card: card.slice(0, 160) }); continue; }
    const href = decodeEntities(urlM[1]);
    const slug = recallKeyFromHref(href);
    if (!slug) { skipped.push({ reason: 'unparseable href', card: href }); continue; }

    const dateM = /<time[^>]*datetime="([^"]+)"/i.exec(card);
    const titleM = /field--name-node-title[\s\S]*?<h2>([\s\S]*?)<\/h2>/i.exec(card);
    const supplier = collapse(fieldText(card, 'field-psa-recall-supplier-name'));

    const categories = [];
    for (const m of card.matchAll(/<a[^>]*href="[^"]*f%5B0%5D=topic%3A(\d+)"[^>]*>([\s\S]*?)<\/a>/gi)) {
      const label = collapse(htmlToText(m[2]));
      if (label) categories.push({ id: Number(m[1]), label });
    }

    out.push({
      slug,
      url: href,
      title: collapse(htmlToText(titleM ? titleM[1] : '')),
      supplier,
      publishedDate: dateM ? dateM[1].slice(0, 10) : null,
      categories,
    });
  }
  return { rows: out, skipped };
}

/**
 * The sale-date range, e.g. "16 August 2025 - 18 December 2025".
 *
 * Returns nulls rather than guessing when the range is absent, open-ended or
 * unparseable — roughly one recall in six has no usable range, and inventing a
 * start date would silently fabricate the "how long was it on the shelf"
 * distribution, which is the whole point of that view.
 */
const MONTHS = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6, july: 7,
  august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9,
  oct: 10, nov: 11, dec: 12,
};

export function parseDayMonthYear(s) {
  if (!s) return null;
  const m = /(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/.exec(s);
  if (!m) return null;
  const day = Number(m[1]);
  const month = MONTHS[m[2].toLowerCase()];
  const year = Number(m[3]);
  if (!month || day < 1 || day > 31 || year < 1900 || year > 2100) return null;
  // Reject impossible calendar dates (31 February) rather than letting Date
  // roll them silently into the next month.
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCDate() !== day || d.getUTCMonth() !== month - 1) return null;
  return d.toISOString().slice(0, 10);
}

export function parseSaleDates(text) {
  const t = collapse(text);
  if (!t) return { start: null, end: null, raw: '' };
  const parts = t.split(/\s+[-–—]\s+/);
  if (parts.length >= 2) {
    return { start: parseDayMonthYear(parts[0]), end: parseDayMonthYear(parts[1]), raw: t };
  }
  return { start: parseDayMonthYear(t), end: null, raw: t };
}

/**
 * `field_psa_recall_location_sold` is a list-string that mixes a national flag,
 * a sales CHANNEL and state names in one field — "Nationally", "Online", "NSW".
 * It is emphatically not a clean state array, and a choropleth built off it
 * unexamined would be a fabricated map. Split it into the three things it
 * actually contains and let the caller keep the remainder visible.
 */
const STATES = new Set(['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT']);
const STATE_ALIASES = {
  'new south wales': 'NSW', victoria: 'VIC', queensland: 'QLD',
  'south australia': 'SA', 'western australia': 'WA', tasmania: 'TAS',
  'northern territory': 'NT', 'australian capital territory': 'ACT',
};

export function parseLocationSold(text) {
  const raw = String(text || '')
    .split('\n')
    .map((s) => collapse(s))
    .filter(Boolean);
  const states = [];
  const channels = [];
  const unresolved = [];
  let national = false;
  for (const v of raw) {
    const up = v.toUpperCase();
    const low = v.toLowerCase();
    if (low.startsWith('nation')) { national = true; continue; }
    if (STATES.has(up)) { if (!states.includes(up)) states.push(up); continue; }
    if (STATE_ALIASES[low]) { const c = STATE_ALIASES[low]; if (!states.includes(c)) states.push(c); continue; }
    if (low === 'online' || low === 'internet') { if (!channels.includes('Online')) channels.push('Online'); continue; }
    unresolved.push(v);
  }
  return { national, states, channels, unresolved, raw };
}

/**
 * The PRA number — "Quote PRA number 2026/20936 when contacting a regulator".
 *
 * This is the register's own citable identifier and the string a reader will be
 * asked for on the phone, so it is worth digging out of the call-out prose it
 * is buried in. It is not present on every recall (older entries predate the
 * scheme), and absence is recorded as null rather than an empty string so the
 * coverage is countable.
 */
export function parsePraNumber(html) {
  const m = /PRA\s*number\s*([0-9]{4}\/[0-9]+)/i.exec(htmlToText(html));
  return m ? m[1] : null;
}

/**
 * The responsible regulator.
 *
 * The field itself renders a glossary definition of the WORD "regulator", not
 * the regulator's name — reading the field block yields the literal label
 * "Responsible regulators", which is how this first parsed. The name is carried
 * on the regulator logo's alt text instead.
 */
export function parseRegulator(html) {
  const block = fieldBlock(html, 'field-psa-regulator-logo')
    ?? fieldBlock(html, 'field-psa-responsible-regulator')
    ?? '';
  const alts = [...block.matchAll(/alt="([^"]+)"/gi)].map((m) => decodeEntities(m[1]));
  const name = alts
    .map((a) => collapse(a.replace(/\s*logo\s*$/i, '')))
    .find((a) => a && !/^product safety$/i.test(a));
  return name || '';
}

/**
 * WHICH REGULATOR ACTUALLY HOLDS THIS RECORD.
 *
 * The ACCC's listing is not homogeneous, and this only became visible because
 * a fifth of the crawled pages came back with no hazard text at all. They are
 * not empty records — they are records belonging to somebody else:
 *
 *   * `fsanz`   — a FOOD recall. The ACCC lists it, but the URL redirects to
 *                 Food Standards Australia New Zealand, and the page fetched is
 *                 FSANZ's generic alerts landing page rather than that recall.
 *                 There is no per-recall hazard text to be had from the ACCC.
 *   * `vehicle` — a VEHICLE recall, redirecting to vehiclerecalls.gov.au and
 *                 rendering that register's `field-r-*` fields. These carry a
 *                 REC- number and DO appear in the vehicle register as well.
 *   * `accc`    — an ordinary consumer product recall, with the psa fields.
 *
 * Reading all three as "an ACCC recall with empty fields" is what produced
 * 1,383 phantom unclassifiable records, and it would have put FSANZ's site
 * navigation into a search index for Australian product recalls.
 */
export function detectRecordSource(html) {
  if (!html) return 'unknown';
  if (/field--name-field-psa-recall-/.test(html)) return 'accc';
  if (/field--name-field-r-(?:defect|hazard|affected-units|pra-number)/.test(html)) return 'vehicle';
  if (/foodstandards\.gov\.au|Food Standards Australia New Zealand/i.test(html)) return 'fsanz';
  return 'unknown';
}

/** Everything the detail page adds on top of the listing row. */
export function parseDetailPage(html) {
  const saleRaw = fieldText(html, 'field-psa-recall-sale-dates');
  const sale = parseSaleDates(saleRaw);
  const location = parseLocationSold(fieldText(html, 'field-psa-recall-location-sold'));

  const categories = [];
  for (const m of html.matchAll(/<a[^>]*href="[^"]*f%5B0%5D=topic%3A(\d+)"[^>]*>([\s\S]*?)<\/a>/gi)) {
    const label = collapse(htmlToText(m[2]));
    const id = Number(m[1]);
    if (label && !categories.some((c) => c.id === id)) categories.push({ id, label });
  }

  const titleM = /<h1[^>]*>([\s\S]*?)<\/h1>/i.exec(html);

  return {
    title: collapse(htmlToText(titleM ? titleM[1] : '')),
    supplier: collapse(fieldText(html, 'field-psa-recall-supplier-name')),
    traders: collapse(fieldText(html, 'field-psa-recall-traders')),
    hazard: fieldText(html, 'field-psa-recall-hazards'),
    defect: fieldText(html, 'field-psa-recall-product-defects'),
    consumerAction: fieldText(html, 'field-psa-recall-consumer-action'),
    productDescription: fieldText(html, 'field-psa-recall-product-desc'),
    country: collapse(fieldText(html, 'field-psa-recall-country-id')),
    regulator: collapse(fieldText(html, 'field-psa-responsible-regulator')),
    saleStart: sale.start,
    saleEnd: sale.end,
    saleRaw: sale.raw,
    location,
    categories,
  };
}

/* ───────────────────────────── Vehicle recalls ───────────────────────────── */

export function parseVehicleListing(html) {
  const ids = [];
  for (const m of html.matchAll(/\/recalls\/(rec-\d+)/gi)) {
    const id = m[1].toUpperCase();
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * The vehicle register renders a flat run of label/value pairs. Reading it as
 * text lines and looking the label up is far more robust than matching its
 * markup, which differs between field groups on the same page.
 */
export function parseVehicleDetail(html) {
  const text = htmlToText(html.replace(/<nav[\s\S]*?<\/nav>/gi, ' '));
  const lines = text.split('\n').map((s) => s.trim()).filter(Boolean);

  const after = (label, { maxLines = 1 } = {}) => {
    const i = lines.findIndex((l) => l.toLowerCase() === label.toLowerCase());
    if (i === -1 || i + 1 >= lines.length) return '';
    return lines.slice(i + 1, i + 1 + maxLines).join(' ').trim();
  };
  // A paragraph field runs until the next known label.
  const KNOWN = new Set([
    'recall number', 'campaign number', 'original published date', 'make', 'model',
    'variants', 'year range', 'affected units', 'identifying features',
    'what are the defects?', 'what are the hazards?', 'what should consumers do?',
    'supplier details', 'responsible regulator', 'product type', 'share this page',
    'who should owners/operators contact for more information?', 'contact name',
    'contact phone', 'contact email', 'contact website', 'make and model information',
    'recall information',
  ]);
  const paragraph = (label) => {
    const i = lines.findIndex((l) => l.toLowerCase() === label.toLowerCase());
    if (i === -1) return '';
    const out = [];
    for (let k = i + 1; k < lines.length; k++) {
      if (KNOWN.has(lines[k].toLowerCase())) break;
      out.push(lines[k]);
    }
    return out.join(' ').trim();
  };

  const id = (after('Recall number') || '').toUpperCase();
  const unitsRaw = after('Affected units');
  const units = /^[\d,]+$/.test(unitsRaw) ? Number(unitsRaw.replace(/,/g, '')) : null;
  const yearRange = after('Year range');
  const ym = /(\d{4})\s*[-–—]\s*(\d{4})/.exec(yearRange) || /(\d{4})/.exec(yearRange);
  const yearFrom = ym ? Number(ym[1]) : null;
  const yearTo = ym ? Number(ym[2] || ym[1]) : null;

  return {
    id: /^REC-\d+$/.test(id) ? id : null,
    campaign: after('Campaign number'),
    published: parseDayMonthYear(after('Original published date')),
    make: collapse(after('Make')),
    model: collapse(after('Model')),
    variants: collapse(after('Variants')),
    yearFrom,
    yearTo,
    units,
    defect: paragraph('What are the defects?'),
    hazard: paragraph('What are the hazards?'),
    consumerAction: paragraph('What should consumers do?'),
    supplier: collapse(after('Supplier details')),
    productType: collapse(after('Product type')),
  };
}

/* ────────────────────────────── cache filenames ──────────────────────────── */

/**
 * A filesystem-safe cache filename for a recall key.
 *
 * Recall slugs are generated from product titles and some of them are enormous
 * — one lists every individually-wrapped pie a bakery makes and runs to 240
 * characters before percent-encoding, which pushes the encoded name past the
 * 255-byte limit every common filesystem enforces. That surfaced as a raw
 * ENAMETOOLONG two thousand pages into a crawl.
 *
 * Long keys therefore get a truncated prefix (still human-readable when you go
 * looking in the cache) plus a hash of the WHOLE key, so two long slugs sharing
 * a prefix cannot collide onto one file and silently overwrite each other.
 * Short keys are unchanged, which keeps every already-cached file valid.
 */
const MAX_ENCODED = 180;

export function cacheFileName(key) {
  const encoded = encodeURIComponent(key).replace(/\*/g, '%2A');
  if (encoded.length <= MAX_ENCODED) return `${encoded}.html`;
  return `${encoded.slice(0, 140)}~${hash32(key)}.html`;
}

/** djb2, hex. Not cryptographic — it only has to avoid accidental collisions. */
export function hash32(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (((h << 5) + h) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(16).padStart(8, '0');
}

/* ─────────────────────────── Supplier normalisation ───────────────────────── */

/**
 * Supplier names are free text with no entity id, so the same company appears
 * under several strings — "Woolworths Ltd" and "Woolworths Limited" are 37 and
 * 35 recalls that must not rank below a company with 71. Normalisation is
 * therefore load-bearing for the leaderboard, which is exactly why the site
 * shows every raw string that was merged into a row rather than hiding it.
 *
 * The rule is deliberately conservative: case, punctuation, and a fixed list of
 * legal-form suffixes only. It never merges on prefix similarity, because
 * "Bunnings Group" and "Bunnings Warehouse" may or may not be one legal entity
 * and guessing would put one company's safety record onto another's name.
 */
const LEGAL_SUFFIXES = [
  'pty ltd', 'pty limited', 'pty. ltd.', 'proprietary limited',
  'limited', 'ltd', 'inc', 'incorporated', 'llc', 'plc', 'gmbh', 'ag', 'nv', 'bv',
  'australia', 'australia pty ltd', 'aust', 'group', 'holdings', 'co', 'company',
  'corporation', 'corp', 'trading as', 't/a', 'the',
];

export function normaliseSupplier(name) {
  let s = collapse(String(name || ''))
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[.,'"“”’()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '';
  // Strip trailing legal forms repeatedly: "Acme Australia Pty Ltd" -> "acme".
  let changed = true;
  while (changed) {
    changed = false;
    for (const suf of LEGAL_SUFFIXES) {
      if (s.endsWith(' ' + suf)) { s = s.slice(0, -(suf.length + 1)).trim(); changed = true; }
    }
  }
  return s.replace(/\s+/g, ' ').trim();
}

/**
 * Tidy a vehicle make for display.
 *
 * The register writes makes inconsistently — "Toyota" and "Holden" sit next to
 * "MITSUBISHI" and "LAND ROVER" in the same column, which reads as a rendering
 * bug rather than as source data.
 *
 * The rule only touches words that are ENTIRELY upper case AND at least four
 * letters long, because the short ones are genuine acronyms: BMW, MG, KTM, GMC,
 * LDV, BYD, UD and MAN are how those marques are actually written, and
 * title-casing them to "Bmw" would be a worse error than the inconsistency it
 * was fixing. Anything already mixed-case is left exactly as the register wrote
 * it.
 */
export function tidyMake(name) {
  const s = collapse(name);
  if (!s) return s;
  return s.split(' ').map((w) => {
    if (w.length < 4) return w;
    if (!/^[A-Z][A-Z'-]*$/.test(w)) return w;
    return w[0] + w.slice(1).toLowerCase();
  }).join(' ');
}

/** Title-cased display form chosen from the raw variants seen for a key. */
export function pickDisplayName(variants) {
  // Prefer the most frequent raw string; break ties on the shortest, then
  // alphabetically, so the choice is deterministic across runs.
  const counts = new Map();
  for (const v of variants) counts.set(v, (counts.get(v) || 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].length - b[0].length || a[0].localeCompare(b[0]))[0][0];
}
